import { UserContext } from '../../domain/models/UserContext';

/**
 * Adapter converts DTO/Plain Object -> Domain model and validates minimal invariants.
 * Applies domain logic (defaults, transformations)
 * Works with both DTO (from API) and plain object (from Redux)
 */
export function adaptUserContextResponseDtoToDomain(
  input: unknown
): UserContext {
  const record: Record<string, unknown> =
    typeof input === 'object' && input !== null ? (input as Record<string, unknown>) : {};

  const isLoggedIn = Boolean(record.isLoggedIn);

  const isSoftLoggedInRaw = record.isSoftLoggedIn;
  const isSoftLoggedIn = typeof isSoftLoggedInRaw === 'boolean' ? isSoftLoggedInRaw : false;

  const softLoginIdRaw = record.softLoginId;
  const softLoginId =
    typeof softLoginIdRaw === 'string' && softLoginIdRaw.trim() ? softLoginIdRaw.trim() : undefined;

  const softLoginTypeRaw = record.softLoginType;
  const softLoginType =
    typeof softLoginTypeRaw === 'string' && softLoginTypeRaw.trim()
      ? softLoginTypeRaw.trim()
      : undefined;

  const visitorIdRaw = record.visitorId;
  const visitorId =
    typeof visitorIdRaw === 'number' && Number.isFinite(visitorIdRaw) ? visitorIdRaw : undefined;

  // Backend uses `customerName`, Redux plain object uses `userName`
  const userNameRaw = (record.customerName ?? record.userName) as unknown;
  const userName = typeof userNameRaw === 'string' && userNameRaw.trim().length > 0 ? userNameRaw.trim() : undefined;

  // Backend uses `cartItemsCount`, Redux plain object uses `cartCount`
  const cartItemsCountRaw = (record.cartItemsCount ?? record.cartCount) as unknown;
  const cartCount = typeof cartItemsCountRaw === 'number' && Number.isFinite(cartItemsCountRaw) ? cartItemsCountRaw : 0;

  // Backend provides design IDs array; Redux plain object stores `wishlistCount`
  const wishListDesignIds = record.wishListDesignIds;
  const wishlistCountRaw =
    Array.isArray(wishListDesignIds) ? wishListDesignIds.length : record.wishlistCount;
  const wishlistCount =
    typeof wishlistCountRaw === 'number' && Number.isFinite(wishlistCountRaw) ? wishlistCountRaw : 0;

  const videoCallTahItemsCountRaw = record.videoCallTahItemsCount as unknown;
  const videoCallTahItemsCount =
    typeof videoCallTahItemsCountRaw === 'number' && Number.isFinite(videoCallTahItemsCountRaw) ? videoCallTahItemsCountRaw : 0;

  const maxVideoCallCountRaw = record.maxVideoCallCount as unknown;
  const maxVideoCallCount =
    typeof maxVideoCallCountRaw === 'number' && Number.isFinite(maxVideoCallCountRaw) ? maxVideoCallCountRaw : 5;

  const maxCartItemsCountRaw = record.maxCartItemsCount as unknown;
  const maxCartItemsCount =
    typeof maxCartItemsCountRaw === 'number' && Number.isFinite(maxCartItemsCountRaw) ? maxCartItemsCountRaw : 3;

  const videoCallCartMessageRaw = record.videoCallCartMessage as unknown;
  const videoCallCartMessage =
    typeof videoCallCartMessageRaw === 'string' && videoCallCartMessageRaw.trim() ? videoCallCartMessageRaw.trim() : undefined;

  const accountMergingPopupEnabledRaw = record.accountMergingPopupEnabled;
  const accountMergingPopupEnabled = typeof accountMergingPopupEnabledRaw === 'boolean' ? accountMergingPopupEnabledRaw : undefined;

  const primaryAccountSetRaw = record.primaryAccountSet;
  const primaryAccountSet = typeof primaryAccountSetRaw === 'boolean' ? primaryAccountSetRaw : undefined;

  const phoneNumberVerifiedRaw = record.phoneNumberVerified;
  const phoneNumberVerified = typeof phoneNumberVerifiedRaw === 'boolean' ? phoneNumberVerifiedRaw : undefined;

  const hasPhoneNumberRaw = record.hasPhoneNumber;
  const hasPhoneNumber = typeof hasPhoneNumberRaw === 'boolean' ? hasPhoneNumberRaw : undefined;

  const pincodeRaw = record.pincode;
  const pincode = typeof pincodeRaw === 'string' && pincodeRaw.trim() ? pincodeRaw.trim() : undefined;

  const guessedPincodeRaw = record.guessedPincode;
  const guessedPincode = typeof guessedPincodeRaw === 'boolean' ? guessedPincodeRaw : undefined;

  return new UserContext({
    isLoggedIn,
    isSoftLoggedIn,
    softLoginId,
    softLoginType,
    userName,
    visitorId,
    videoCallCartMessage,
    accountMergingPopupEnabled,
    primaryAccountSet,
    phoneNumberVerified,
    hasPhoneNumber,
    pincode,
    guessedPincode,
    cartCount,
    wishlistCount,
    videoCallTahItemsCount,
    maxVideoCallCount,
    maxCartItemsCount,
  });
}

/**
 * Convert domain model (class instance) to plain object for Redux serialization
 */
export function userContextToPlainObject(userContext: UserContext): Record<string, unknown> {
  return {
    isLoggedIn: userContext.isLoggedIn,
    isSoftLoggedIn: userContext.isSoftLoggedIn,
    softLoginId: userContext.softLoginId,
    softLoginType: userContext.softLoginType,
    userName: userContext.userName,
    customerName: userContext.customerName,
    visitorId: userContext.visitorId,
    cartCount: userContext.cartCount,
    wishlistCount: userContext.wishlistCount,
    videoCallTahItemsCount: userContext.videoCallTahItemsCount,
    maxVideoCallCount: userContext.maxVideoCallCount,
    maxCartItemsCount: userContext.maxCartItemsCount,
    accountMergingPopupEnabled: userContext.accountMergingPopupEnabled,
    primaryAccountSet: userContext.primaryAccountSet,
    phoneNumberVerified: userContext.phoneNumberVerified,
    hasPhoneNumber: userContext.hasPhoneNumber,
    pincode: userContext.pincode,
    guessedPincode: userContext.guessedPincode,
  };
}

